import { useCallback, useEffect, useState } from 'react';
import { resolveOrganizationBrandName } from '../../utils/branding';
import { useI18n } from '../../context/useI18n';
import { useAuth } from '../../context/useAuth';
import api from '../../services/api';
import GymImportStructuresPanel from './GymImportStructuresPanel';

const OPERATION_CARDS = [
  {
    id: 'gym-masters-programs',
    titleKey: 'gym.masters.programs',
    fallback: 'Programas',
    descKey: 'gym.masters.programs_desc',
    descFallback: 'Programas propios de tu sede para vincular a ciclos, planes y clases.',
  },
  {
    id: 'gym-masters-cycles',
    title: 'Ciclos',
    titleKey: 'gym.masters.cycles',
    fallback: 'Ciclos',
    descKey: 'gym.masters.cycles_desc',
    descFallback: 'Calendario de ciclos propios de tu sede (inicio y fin personalizables).',
  },
  {
    id: 'gym-masters-plans',
    title: 'Planes comerciales',
    titleKey: 'gym.masters.plans',
    fallback: 'Planes comerciales',
    descKey: 'gym.masters.plans_desc',
    descFallback: 'Crea planes de suscripción y asígnalos a tus programas y ciclos.',
  },
  {
    id: 'gym-masters-routines',
    title: 'Rutinas',
    titleKey: 'gym.masters.routines',
    fallback: 'Rutinas',
    descKey: 'gym.masters.routines_desc',
    descFallback: 'Plantillas de rutinas propias de tu sede para clases en vivo.',
  },
  {
    id: 'adminusers',
    titleKey: 'gym.masters.users',
    fallback: 'Usuarios',
    descKey: 'gym.masters.users_desc',
    descFallback: 'Usuarios finales, sedes marca blanca e invitaciones.',
  },
  {
    id: 'gym-masters-trainers',
    titleKey: 'gym.masters.trainers',
    fallback: 'Entrenadores',
    descKey: 'gym.masters.trainers_desc',
    descFallback: 'Entrenadores de tu sede para clases en vivo, rutinas y Zoom.',
  },
  {
    id: 'adminliveclasses',
    titleKey: 'nav.liveclasses',
    fallback: 'Clases en vivo',
    descKey: 'gym.masters.live_desc',
    descFallback: 'Programar clases en vivo y registro de asistencia.',
  },
  {
    id: 'd28d-gallery',
    titleKey: 'nav.gallery_classes',
    fallback: 'GRABACIONES DE CLASE',
    descKey: 'gym.masters.gallery_desc',
    descFallback: 'Grabaciones de clase on-demand de tu sede, con contador de visualizaciones.',
  },
];

const CONFIG_CARDS = [
  {
    id: 'gym-mi-marca',
    titleKey: 'gym.mi_marca.title',
    fallback: 'Mi Marca',
    descKey: 'gym.masters.brand_desc',
    descFallback: 'Nombre, logo, colores, contacto y URL pública.',
  },
  {
    id: 'communication',
    titleKey: 'nav.communication',
    fallback: 'Comunicación',
    descKey: 'gym.masters.comm_desc',
    descFallback: 'Plantillas email/WhatsApp y prueba de envíos (Fit Live, Food, Training).',
  },
  {
    id: 'modulevigencias',
    titleKey: 'nav.vigencias',
    fallback: 'Licencia',
    descKey: 'gym.masters.vigencias_desc',
    descFallback: 'Vigencias, pagos pendientes y renovaciones.',
  },
];

function asArray(payload) {
  if (Array.isArray(payload)) return payload;
  if (payload && typeof payload === 'object') {
    if (Array.isArray(payload.data)) return payload.data;
    if (Array.isArray(payload.gyms)) return payload.gyms;
  }
  return [];
}

function CardGrid({ cards, onNavigate, t }) {
  return (
    <div className="services-hero-grid">
      {cards.map((card) => (
        <button
          key={card.id}
          type="button"
          className="service-card-hero"
          onClick={() => onNavigate?.(card.id)}
          style={{ textAlign: 'left', border: 'none', padding: 0, background: 'transparent', cursor: 'pointer' }}
        >
          <div className="service-card-hero-content" style={{ padding: '1.25rem' }}>
            <h3 className="service-card-hero-title notranslate" translate="no">
              {card.title || t(card.titleKey, card.fallback)}
            </h3>
            <p className="service-card-hero-desc">{t(card.descKey, card.descFallback)}</p>
          </div>
        </button>
      ))}
    </div>
  );
}

/** Maestros operativos del gimnasio virtual (panel unificado por sede). */
export default function GymMastersHub({
  onNavigate,
  onBack = null,
  scopeGymId = null,
  scopeGymName = null,
}) {
  const { t } = useI18n();
  const { user } = useAuth();
  const [gym, setGym] = useState(null);

  const resolvedGymId = scopeGymId || user?.gym_id || user?.gymId || null;

  const loadGym = useCallback(async () => {
    if (!resolvedGymId) {
      setGym(null);
      return;
    }
    try {
      const [listRes, brandRes] = await Promise.all([
        api.get('/gyms'),
        api.get(`/gyms/${resolvedGymId}/branding`).catch(() => null),
      ]);
      const list = asArray(listRes.data);
      const mine = list.find((g) => String(g.id) === String(resolvedGymId)) || null;
      const branding = brandRes?.data?.branding || {};
      setGym(mine ? { ...mine, ...branding } : null);
    } catch {
      setGym(null);
    }
  }, [resolvedGymId]);

  useEffect(() => {
    loadGym();
  }, [loadGym]);

  const gymLabel = scopeGymName || resolveOrganizationBrandName({ entity: gym }) || t('gym.masters.your_gym', 'Tu gimnasio virtual');
  const plansCount = Array.isArray(gym?.available_plan_ids) ? gym.available_plan_ids.length : 0;

  return (
    <div className="dashboard-main-view space-y-8">
      <header className="services-hero">
        {onBack ? (
          <button type="button" className="btn-secondary panel-back-btn mb-3" onClick={onBack}>
            {t('tenants.back_list', '← Gimnasios virtuales')}
          </button>
        ) : null}
        <h2 className="services-hero-title">
          {t('tenants.panel_title', 'PANEL')}{' '}
          <span>{gymLabel}</span>
        </h2>
        <p className="services-hero-subtitle">
          {t('gym.masters.subtitle', 'Operación diaria y configuración de tu sede en un solo lugar.')}
        </p>
        {gym ? (
          <p className="text-sm text-stone-600 mt-2">
            <strong>{gymLabel}</strong>
            {gym.email ? ` · ${gym.email}` : ''}
            {gym.support_whatsapp ? ` · ${gym.support_whatsapp}` : ''}
            {plansCount > 0
              ? ` · ${plansCount} ${t('gym.masters.plans_active', 'planes activos')}`
              : ` · ${t('gym.masters.plans_pending', 'sin planes configurados aún')}`}
          </p>
        ) : null}
      </header>

      <GymImportStructuresPanel scopeGymId={resolvedGymId} />

      <section className="space-y-3">
        <h3 className="text-lg font-semibold text-stone-900">
          {t('gym.masters.section_operation', 'Operación diaria')}
        </h3>
        <CardGrid cards={OPERATION_CARDS} onNavigate={onNavigate} t={t} />
      </section>

      <section className="space-y-3">
        <h3 className="text-lg font-semibold text-stone-900">
          {t('gym.masters.section_config', 'Configuración')}
        </h3>
        <CardGrid cards={CONFIG_CARDS} onNavigate={onNavigate} t={t} />
      </section>
    </div>
  );
}
